import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { message } from 'antd';
import { AnyAction } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';

import MenuIcon from 'components/Header/MenuIcon';
import { useTenant } from 'hooks';

import { AiChatBotBody, AiChatBotHeader, AIChatBotWrapper } from './AiChatBot.styles';
import ChatMessageInput from './ChatMessageInput';
import ChatMessageWrapper from './ChatMessageWrapper';
import { restartAiChat, sendAiChatMessage } from './actions';
import { aiChatBotApi } from './aiChatBotApi';
import {
  AI_CHATBOT_SEND_MESSAGE,
  AI_CHATBOT_RECEIVE_MESSAGE,
  AI_CHATBOT_SET_TOKENS,
  AI_CHATBOT_SET_OPEN
} from './reducer';

const AiChatBot: React.FC = () => {
  const { name: tenantName } = useTenant();
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const open = useSelector((state: any) => state.aiChatBot.open);
  const messages = useSelector((state: any) => state.aiChatBot.messages);
  const loading = useSelector((state: any) => state.aiChatBot.loading);
  const tokens = useSelector((state: any) => state.aiChatBot.tokens);

  const handleRetry = () => {
    const lastUserMsgObj = [...messages].reverse().find(m => m.sender === 'user');
    const lastUserMsg = lastUserMsgObj?.text || '';
    const lastErrorIdx = [...messages]
      .reverse()
      .findIndex(m => m.errorType === 'api' || m.errorType === 'token');
    let newMessages;
    if (lastErrorIdx === -1) {
      newMessages = [...messages, { text: '__LOADING_DOTS__', sender: 'bot' }];
    } else {
      const idxToRemove = messages.length - 1 - lastErrorIdx;
      newMessages = messages.filter((_, idx) => idx !== idxToRemove);
      newMessages = [...newMessages, { text: '__LOADING_DOTS__', sender: 'bot' }];
    }
    dispatch({ type: AI_CHATBOT_RECEIVE_MESSAGE, payload: newMessages });
    if (lastUserMsg) {
      dispatch(sendAiChatMessage(lastUserMsg, newMessages, tenantName));
    }
  };

  const handleReStartChat = () => {
    const newMessages = [messages[0]];
    dispatch(restartAiChat(newMessages));
  };

  const handleSendMessage = (message: { text: string; sender: string }) => {
    if (loading) return;
    let newMessages = [...messages, message];
    dispatch({ type: AI_CHATBOT_SEND_MESSAGE, payload: message });
    if (message.sender === 'user') {
      newMessages = [...newMessages, { text: '__LOADING_DOTS__', sender: 'bot' }];
      dispatch({
        type: AI_CHATBOT_SEND_MESSAGE,
        payload: { text: '__LOADING_DOTS__', sender: 'bot' }
      });
      dispatch(sendAiChatMessage(message.text, newMessages, tenantName));
    }
  };

  const fetchTokenUsage = async () => {
    try {
      const response = await aiChatBotApi('/resources/token-usage', {
        method: 'GET'
      });
      if (response.data.success) {
        dispatch({
          type: AI_CHATBOT_SET_TOKENS,
          payload: {
            total_tokens: response.data.data.max_tokens,
            unused_tokens: response.data.data.remaining_tokens
          }
        });
      }
    } catch (e) {
      message.error('Failed to fetch token usage.');
    }
  };

  useEffect(() => {
    fetchTokenUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AIChatBotWrapper open={open}>
      {open && (
        <AiChatBotBody open={open}>
          <AiChatBotHeader>
            <div>
              <MenuIcon src={'sparkle'} width={28} height={28} />
              <h3>Perx eMind</h3>
              <span>{`${tokens.unused_tokens}/${tokens.total_tokens}`} tokens</span>
            </div>
            <button onClick={() => dispatch({ type: AI_CHATBOT_SET_OPEN, payload: false })}>
              <MenuIcon src={'close'} width={20} height={20} />
            </button>
          </AiChatBotHeader>
          <ChatMessageWrapper
            messages={messages}
            onSuggestionClick={handleSendMessage}
            onRetry={handleRetry}
          />
          <ChatMessageInput
            submitMessage={handleSendMessage}
            handleReStartChat={handleReStartChat}
          />
        </AiChatBotBody>
      )}
    </AIChatBotWrapper>
  );
};

export default AiChatBot;
